import Calendar from '@mui/icons-material/CalendarToday';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import FilterCenterFocusOutlinedIcon from '@mui/icons-material/FilterCenterFocusOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import List from '@mui/icons-material/List';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaymentsIcon from '@mui/icons-material/Payments';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';

const CompReportsSidebar = ({
  reports,
  isExpanded,
  toggleExpanded,
  isLoading,
  onOpenPDF,
  onLoadSession,
  onEditReport,
  onViewAllReports,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Helper function to get counts from row_order
  const getCounts = (rowOrder) => {
    try {
      // Handle case where rowOrder might be a string or already parsed object
      const parsedRowOrder =
        typeof rowOrder === 'string' ? JSON.parse(rowOrder) : rowOrder || {};
      return {
        offers: parsedRowOrder.offers?.length || 0,
        feedback: parsedRowOrder.feedback?.length || 0,
      };
    } catch (e) {
      console.error('Error parsing row_order:', e);
      return { offers: 0, feedback: 0 };
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom:
          isMobile && isExpanded ? 'env(safe-area-inset-bottom, 0px)' : 16,
        left: isMobile && isExpanded ? 'env(safe-area-inset-left, 0px)' : 16,
        right:
          isMobile && isExpanded ? 'env(safe-area-inset-right, 0px)' : 'auto',
        top: isMobile && isExpanded ? 'env(safe-area-inset-top, 0px)' : 'auto',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        pointerEvents: isExpanded ? 'auto' : 'none',
      }}
    >
      {isExpanded && (
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: isMobile ? 0 : '8px',
            boxShadow: 3,
            width: isMobile ? '100%' : '400px',
            height: isMobile ? '100vh' : 'auto',
            maxHeight: isMobile ? '100vh' : '60vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              position: isMobile ? 'static' : 'relative',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsertDriveFileIcon fontSize='small' />
              <Typography variant='subtitle1'>
                Published Listing Reports
              </Typography>
            </Box>
            <IconButton
              onClick={toggleExpanded}
              size='small'
              sx={{ color: 'white' }}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                p: 3,
                display: 'flex',
                justifyContent: 'center',
                flex: 1,
                overflowY: 'auto',
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  overflowY: 'auto',
                  flex: 1,
                  maxHeight: isMobile ? 'calc(100vh - 112px)' : 'none',
                }}
              >
                {reports.map((report) => {
                  const counts = getCounts(report.row_order);
                  return (
                    <Box
                      key={report.report_id}
                      sx={{
                        p: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { backgroundColor: 'action.hover' },
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ pr: 6 }}>
                        <Typography
                          variant='body1'
                          color='primary'
                          fontWeight='medium'
                        >
                          {report.title}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 2,
                            mt: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Calendar fontSize='small' />
                            {formatDate(report.created_at)}
                          </Typography>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <PaymentsIcon fontSize='small' />
                            {counts.offers} offers
                          </Typography>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <ChatIcon fontSize='small' />
                            {counts.feedback} feedback
                          </Typography>
                        </Box>
                        <Typography variant='caption' color='text.secondary'>
                          Created by {report.user_id}
                        </Typography>
                      </Box>

                      {/* Action buttons */}
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '80%',
                          transform: 'translateY(-50%)',
                          display: 'flex',
                          gap: 1,
                        }}
                      >
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenPDF(report);
                          }}
                          title='View PDF Report'
                        >
                          <OpenInNewIcon fontSize='small' />
                        </IconButton>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation();
                            onLoadSession(report);
                          }}
                          title='Fly to Location'
                        >
                          <RoomOutlinedIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Box
                sx={{
                  p: 1,
                  backgroundColor: 'background.paper',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center',
                  flexShrink: 0,
                }}
              >
                <Button
                  size='small'
                  startIcon={<List fontSize='small' />}
                  sx={{ textTransform: 'none' }}
                  onClick={onViewAllReports}
                >
                  View All Listing Reports
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CompReportsSidebar;
